import { Warehouse, WarehouseOption, CreateWarehouseRequest, UpdateWarehouseRequest } from '../types/warehouse';
import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export class WarehouseService {
  private baseUrl = `${API_BASE_URL}/warehouse`;

  /**
   * Get all warehouses for current tenant
   */
  async getWarehouses(): Promise<Warehouse[]> {
    // Get company number from auth token
    const userContext = AuthService.getUserDataFromToken();
    if (!userContext) {
      throw new Error('No user context available');
    }
    
    const url = `${API_BASE_URL}/${userContext.companyNo}/warehouse`;
    console.log('🚀 Making API call to:', url);
    
    try {
      const response = await AuthService.authenticatedGet(url);

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('🚨 Response error:', errorData);
        throw new Error(extractApiError(errorData, response, 'HTTP error!'));
      }

      const warehouses = await response.json();
      console.log('📦 Raw response data:', warehouses);
      
      const result = Array.isArray(warehouses) ? warehouses : [];
      console.log('✅ Final processed warehouses:', result);
      
      return result;
    } catch (error) {
      console.error('🚨 Fetch error in getWarehouses:', error);
      console.error('🚨 Error name:', error instanceof Error ? error.name : 'Unknown');
      console.error('🚨 Error message:', error instanceof Error ? error.message : error);
      console.error('🚨 Error stack:', error instanceof Error ? error.stack : 'No stack');
      throw new Error(`Failed to fetch warehouses: ${error instanceof Error ? error.message : error}`);
    }
  }

  /**
   * TW 36430604: lagerlåsen for innlogget operatør (standardlageret når låst).
   */
  async getAccess(): Promise<{ isLocked: boolean; warehouseId: string | null }> {
    const userContext = AuthService.getUserDataFromToken();
    if (!userContext) {
      return { isLocked: false, warehouseId: null };
    }
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/${userContext.companyNo}/warehouse/access`);
    if (!response.ok) {
      throw new Error(`Failed to fetch warehouse access: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Get warehouse by company and warehouse ID
   */
  async getWarehouse(companyNo: string, warehouseId: string): Promise<Warehouse | null> {
    try {
      const response = await AuthService.authenticatedGet(`${API_BASE_URL}/${companyNo}/warehouse/${warehouseId}`);

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching warehouse:', error);
      throw new Error('Failed to fetch warehouse');
    }
  }

  /**
   * Get all warehouses for a specific company
   */
  async getWarehousesByCompany(companyNo: string): Promise<Warehouse[]> {
    try {
      const response = await AuthService.authenticatedGet(`${API_BASE_URL}/${companyNo}/warehouse`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const warehouses = await response.json();
      return Array.isArray(warehouses) ? warehouses : [];
    } catch (error) {
      console.error('Error fetching warehouses by company:', error);
      throw new Error('Failed to fetch warehouses by company');
    }
  }

  /**
   * Convert warehouse data to dropdown options
   */
  convertToOptions(warehouses: Warehouse[]): WarehouseOption[] {
    return warehouses.map(warehouse => ({
      value: warehouse.warehouseID,
      label: `${warehouse.warehouseDescr} (${warehouse.warehouseID})`,
      description: warehouse.warehouseDescr
    }));
  }

  /**
   * Get warehouses as dropdown options for current tenant
   */
  async getWarehouseOptions(): Promise<WarehouseOption[]> {
    try {
      console.log('🔄 Starting to fetch warehouse options...');
      console.log('🔧 API Base URL:', API_BASE_URL);
      
      const userContext = AuthService.getUserDataFromToken();
      if (!userContext) {
        throw new Error('No user context available');
      }
      
      console.log('🔧 Full warehouse URL:', `${API_BASE_URL}/${userContext.companyNo}/warehouse`);
      console.log('🔧 Auth token exists:', !!AuthService.getToken());
      
      const warehouses = await this.getWarehouses();
      console.log('✅ Successfully fetched warehouses from API:', warehouses);
      
      const options = this.convertToOptions(warehouses);
      console.log('✅ Converted to options:', options);
      
      return options;
    } catch (error) {
      console.error('❌ Error fetching warehouse options:', error);
      console.error('❌ Full error details:', JSON.stringify(error, null, 2));
      console.log('⚠️ Falling back to hardcoded options');
      
      // Return empty array on API failure - consumers should handle empty state
      return [];
    }
  }

  /**
   * Create a new warehouse
   */
  async createWarehouse(companyNo: string, request: CreateWarehouseRequest): Promise<Warehouse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/warehouse`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to create warehouse'));
    }

    return await response.json();
  }

  /**
   * Update an existing warehouse
   */
  async updateWarehouse(
    companyNo: string,
    code: string,
    request: UpdateWarehouseRequest
  ): Promise<Warehouse> {
    const encodedCode = encodeURIComponent(code);
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/${companyNo}/warehouse/${encodedCode}`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update warehouse'));
    }

    return await response.json();
  }

  /**
   * Delete a warehouse
   */
  async deleteWarehouse(companyNo: string, code: string): Promise<void> {
    const encodedCode = encodeURIComponent(code);
    const response = await AuthService.authenticatedFetch(
      `${API_BASE_URL}/${companyNo}/warehouse/${encodedCode}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to delete warehouse'));
    }
  }

  /**
   * Check if warehouse is in advanced mode (UseAWYesNo = true)
   */
  async isAdvancedWarehouse(warehouseId: string): Promise<boolean> {
    const userContext = AuthService.getUserDataFromToken();
    if (!userContext) {
      throw new Error('No user context available');
    }

    try {
      const warehouse = await this.getWarehouse(userContext.companyNo, warehouseId);
      // Return true if UseAWYesNo is true, otherwise false (Standard mode)
      return warehouse?.useAWYesNo ?? false;
    } catch (error) {
      console.error('Error checking warehouse mode:', error);
      // Default to advanced mode on error to ensure full functionality
      return true;
    }
  }
}

// Export singleton instance
export const warehouseService = new WarehouseService();
